import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styled, { css, ThemeProvider } from 'styled-components';
import { withTheme } from '@material-ui/core/styles';
import { palette } from 'theme';
import darken from 'polished/lib/color/darken';
import _ from 'lodash';

import { setSelectedFilters } from 'features/filter/actions';

import Badge from '@material-ui/core/Badge';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';

import ChevronUpIcon from 'mdi-material-ui/ChevronUp';

const StyledToolbar = styled(Toolbar)`
  flex-wrap: wrap;
`;

const LabelTypography = styled(Typography)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FilterBadge = styled(Badge)`
  & .MuiBadge-dot-419 {
    top: 7px;
    left: -18px;
    right: initial;
  }
`;

const FilterListLabel = styled(ListSubheader)`
  padding-bottom: 5px;
  position: static;
  cursor: pointer;
  &:hover ${LabelTypography} {
    color: ${palette.primary.main};
  }
`;

const ExpandMoreIcon = styled(ChevronUpIcon)`
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  transform: rotate(${props => (props.in ? '0deg' : '180deg')});
`;

const FilterButtonLabel = styled.span`
  margin-right: 10px;
  pointer-events: none;
`;

const FilterButton = styled(Button)`
  color: ${props => props.theme.palette.text.primary};
  border-radius: 20px;
  margin: 8px 16px 8px 0;

  background-color: ${props =>
    props.theme.palette.type === 'light'
      ? 'rgba(0, 100, 100, 0.05)'
      : palette.grey[900]};

  ${props =>
    (props.selected || props.active) &&
    css`
      background-color: ${props =>
        props.theme.palette.type === 'light'
          ? 'rgba(0, 50, 50, 0.15)'
          : darken(0.3, palette.grey[900])};
    `} &:hover {
    background-color: ${props =>
      props.theme.palette.type === 'light'
        ? 'rgba(0, 50, 50, 0.2)'
        : darken(0.6, palette.grey[900])};
  }
`;

const FilterListItemText = styled(ListItemText)``;

const FilterListItem = styled(({ selected, ...other }) => (
  <ListItem dense {...other} />
))`
  cursor: pointer;
  &:focus,
  &:hover {
    background-color: #e0e7e9;
  }
  ${props =>
    props.selected &&
    css`
      border-left: 3px solid ${palette.primary.main};
      ${FilterListItemText} {
        span {
          color: ${palette.primary.main};
        }
      }
    `}
`;

const Filter = ({
  items,
  label,
  filterKey,
  matchKey,
  itemLabelKey,
  setSelectedFilters,
  selectedFilters,
  variant = 'button',
  showAll = true,
  allLabel = 'All',
  collapsedOnLoad = false
}) => {
  const [isActive, setIsActive] = useState(_.get(selectedFilters, filterKey));

  const [isCollapsed, setIsCollapsed] = useState(
    isActive ? !isActive : collapsedOnLoad
  );

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  const components = {
    filterContainer: { list: List, button: FilterToolbar },
    filterLabel: { list: FilterListLabel, button: FilterButtonLabel },
    filterItem: { list: FilterListItem, button: FilterButton },
    filterItemText: { list: FilterListItemText, button: 'span' }
  };

  const FilterContainer = components['filterContainer'][variant];
  const FilterLabel = components['filterLabel'][variant];
  const FilterItem = components['filterItem'][variant];
  const FilterItemText = components['filterItemText'][variant];

  useEffect(() => {
    setIsActive(_.get(selectedFilters, filterKey));
  }, [selectedFilters, filterKey]);

  return (
    <FilterContainer>
      <FilterLabel onClick={toggleCollapse}>
        <LabelTypography variant="overline">
          <FilterBadge
            color="primary"
            variant="dot"
            invisible={!(isCollapsed && isActive)}
          >
            <span>{label}</span>
          </FilterBadge>
          {variant === 'list' && <ExpandMoreIcon in={!isCollapsed ? 1 : 0} />}
        </LabelTypography>
      </FilterLabel>

      <Collapse in={!isCollapsed}>
        {showAll && (
          <FilterItem
            selected={!isActive}
            onClick={() => {
              setSelectedFilters(delete selectedFilters[filterKey]);
            }}
          >
            <FilterItemText>{allLabel}</FilterItemText>
          </FilterItem>
        )}
        {items.map((filterItem, idx) => {
          const isSelected =
            _.get(selectedFilters, filterKey) ===
            (matchKey ? _.get(filterItem, matchKey) : filterItem);

          return (
            <FilterItem
              selected={isSelected}
              key={filterItem.uid}
              onClick={() => {
                if (isSelected) {
                  setSelectedFilters(delete selectedFilters[filterKey]);
                } else {
                  setSelectedFilters({
                    [filterKey]: matchKey
                      ? _.get(filterItem, matchKey)
                      : filterItem
                  });
                }
              }}
            >
              <FilterItemText>
                {itemLabelKey ? _.get(filterItem, itemLabelKey) : filterItem}
              </FilterItemText>
            </FilterItem>
          );
        }, this)}
      </Collapse>
    </FilterContainer>
  );
};

const FilterToolbar = withTheme()(props => (
  <ThemeProvider theme={props.theme}>
    <StyledToolbar
      theme={props.theme}
      className={props.className}
      disableGutters
    >
      {props.children}
    </StyledToolbar>
  </ThemeProvider>
));

export default connect(
  state => ({
    selectedFilters: state.filters.selected_filters
  }),
  {
    setSelectedFilters
  }
)(Filter);

export { FilterToolbar, FilterButton };
